/**
 * Cinematic imagery layer for the landing + blog surfaces.
 *
 * Every generated image is served as a static asset under `/img/…` (see
 * apps/web/public/img). These components render that image when it exists and
 * degrade GRACEFULLY when it does not: the hero falls back to the animated SVG
 * emblem, blog covers fall back to a branded gradient + kicker. So the page
 * looks finished whether or not the AI art has been generated yet — dropping a
 * file into public/img lights the slot up with no code change.
 */

import { useState } from "react";
import { HeroEmblem } from "./mockups.js";
import { FlowIllustration } from "./illustrations.js";

/** Static asset paths — one image per blog post key, one hero, one band. */
export const heroImgSrc = "/img/hero.webp";
export const heroVideoSrc = "/img/hero-bg.mp4";
export const heroVideoPoster = "/img/hero-bg.webp";
export const bandImgSrc = "/img/band.webp";
export const introVideoSrc = "/img/intro.mp4";
export const flowVideoSrc = "/img/flow.mp4";
export const flowPosterSrc = "/img/flow-poster.webp";
export const blogImgSrc = (key: string): string => `/img/blog/${key}.webp`;

/**
 * Full-bleed hero background: a muted, looping cinematic clip behind the hero
 * headline. Under prefers-reduced-motion it shows the poster still instead. The
 * scrim gradient keeps the overlaid copy readable regardless of the frame. Purely
 * decorative, so aria-hidden.
 */
export function HeroVideoBg() {
  const reduceMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return (
    <div className="ld-herocine-bg" aria-hidden="true">
      {reduceMotion ? (
        <img className="ld-herocine-media" src={heroVideoPoster} alt="" />
      ) : (
        <video
          className="ld-herocine-media"
          poster={heroVideoPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src={heroVideoSrc} type="video/mp4" />
        </video>
      )}
      <div className="ld-herocine-scrim" />
    </div>
  );
}

/**
 * The "data flow" bento visual. By default it plays the Higgsfield-generated
 * cinematic loop of the product→QR→passport→role flow (muted, looping). Under
 * prefers-reduced-motion it renders the hand-built animated SVG diagram instead
 * — crisp, vector, and motion-safe. The generated still is the video poster, so
 * a missing clip degrades to a static frame rather than an empty box.
 */
export function FlowMedia({ lang }: { lang: "tr" | "en" }) {
  const reduceMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion) return <FlowIllustration lang={lang} />;
  return (
    <div className="ld-flow-media">
      <video
        className="ld-flow-video"
        poster={flowPosterSrc}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
      >
        <source src={flowVideoSrc} type="video/mp4" />
      </video>
    </div>
  );
}

/**
 * Hero centerpiece: the generated cinematic passport image fades in over the
 * animated emblem. While loading, the emblem shows; if the file is absent the
 * emblem simply stays — no broken-image artifact.
 */
export function HeroMedia() {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div className="ld-hero-media">
      {(!loaded || failed) && <HeroEmblem />}
      {!failed && (
        <img
          src={heroImgSrc}
          alt=""
          aria-hidden="true"
          className={`ld-hero-img${loaded ? " is-loaded" : ""}`}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

/**
 * Blog cover art. Renders `/img/blog/<key>.webp` when present, otherwise a
 * branded indigo gradient tile stamped with the post's tag kicker — an
 * intentional-looking placeholder rather than an empty box.
 */
export function BlogCover({
  postKey,
  kicker,
  className,
  eager = false,
}: {
  postKey: string;
  kicker?: string | undefined;
  className?: string | undefined;
  eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`blog-cover${className ? ` ${className}` : ""}`}>
      {failed ? (
        <div className="blog-cover-fallback">
          <span className="blog-cover-grid" aria-hidden="true" />
          {kicker ? <span className="blog-cover-kicker">{kicker}</span> : null}
        </div>
      ) : (
        <img
          src={blogImgSrc(postKey)}
          alt=""
          aria-hidden="true"
          className="blog-cover-img"
          loading={eager ? "eager" : "lazy"}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

/**
 * Full-width cinematic band — a wide moving strip that adds visual rhythm below
 * the hero. It plays the generated intro video (muted, looping); the band image
 * is the poster, so if `intro.mp4` is absent the still image shows instead. Under
 * prefers-reduced-motion it renders the still image only. If even the image is
 * missing, the video's dark stage remains — a graceful, layered fallback chain.
 */
export function CinematicBand({ caption }: { caption?: string | undefined }) {
  const reduceMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return (
    <div className="ld-imgband">
      {reduceMotion ? (
        <img src={bandImgSrc} alt="" aria-hidden="true" className="ld-imgband-img" loading="lazy" />
      ) : (
        <video
          className="ld-imgband-img"
          poster={bandImgSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src={introVideoSrc} type="video/mp4" />
        </video>
      )}
      {caption ? <span className="ld-imgband-cap">{caption}</span> : null}
    </div>
  );
}
